import { ChangeEvent, useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import cssns from "../../../../../shared/utils/cssnsConfig";
import { Package } from "../../../../../graphql/types";
import { createMarkup } from "../../../../../shared/utils/createMarkup";
import { PackagePrices } from "../../../../packages/PackagePrice";
import {config, RECURRENT} from "../../../../../config/constants";
import {checkIfPaidGigabitPack, checkIfSBGigabitPack, isGPONFTTBPack, isONUFTTBPack, extractFee} from "../../../../../shared/utils";

const { React } = cssns("Optional-Product");

const PAID_GIGABIT_CODE = "FTTBGIGABIT";

interface OptionProductProps {
    optionalProduct: Package;
    onChecked: (value: OptionProductChecked) => void;
    selectedOptionalProduct: Package[];
	familyName?: string|null|undefined;
	disabled?: boolean;
}
export interface OptionProductChecked {
    optionalProduct: Package;
    checked: boolean;
	familyName?: string|null|undefined;
	disabled?:boolean
}
const OptionProduct = (props: OptionProductProps) => {
	const intl = useIntl();
	const [isChecked, setIsChecked] = useState(false);
	const[disableRadioForGigabit, setDisableRadioForGigabit] = useState(false);
	const[disableCheckboxForGigabit, setDisableCheckboxForGigabit] = useState(false);
	const { optionalProduct, familyName, selectedOptionalProduct, onChecked } = props;
	const onChange = (value: Package, familyName: string|null|undefined) => (event: ChangeEvent<HTMLInputElement>) => {
		onChecked({ optionalProduct: value, checked: event.target.checked, familyName });
	};
	const checkboxOptionId = "id-select-" + optionalProduct.code;
	const isGigabitProduct = checkIfPaidGigabitPack(optionalProduct);
	const isOptionalGPONFTTBPack = isGPONFTTBPack(optionalProduct);
	const isOptionalONUFTTBPack = isONUFTTBPack(optionalProduct);
	const optionalProductIsSelected = Boolean(selectedOptionalProduct.find(item => item.code === optionalProduct.code));

	const isGigabitSelected = Boolean(selectedOptionalProduct.find(item => item.code === PAID_GIGABIT_CODE));
	const isGponFttbSelected = Boolean(selectedOptionalProduct.find(item => item.code === config.GPON_FTTB_PACKAGE_CODE));
	const isAutoSelectedProductMissing =
		(!isGponFttbSelected && isOptionalGPONFTTBPack) ||
		(isGigabitProduct && !isGigabitSelected);
	const shouldAutoSelect =
		isAutoSelectedProductMissing ||
		(isOptionalONUFTTBPack && isGponFttbSelected && !optionalProductIsSelected);
	const checked = (isOptionalGPONFTTBPack && !isGponFttbSelected) || (isGigabitProduct && !isGigabitSelected) ? isChecked : optionalProductIsSelected;
	const hasAutoSelectedRef = useRef(false);
	

	useEffect(() => {
		// check the checkbox by default if it has to be auto-selected
		if (shouldAutoSelect && !hasAutoSelectedRef.current && !isChecked && !checked) {
			hasAutoSelectedRef.current = true;
			setIsChecked(true);
			onChecked({
				optionalProduct,
				checked: true,
				familyName,
			});
		}
	}, [shouldAutoSelect, isChecked, checked, optionalProduct, familyName, onChecked]);

	useEffect(() => {
		const isOptionalProductSBGigabit = checkIfSBGigabitPack(optionalProduct);
		const isOptionalProductPaidGigabit = checkIfPaidGigabitPack(optionalProduct);
		const isOptionalGPONFTTBPack = isGPONFTTBPack(optionalProduct);

		const hasPaidGigabitSelected = selectedOptionalProduct.some(
			selectedProduct => checkIfPaidGigabitPack(selectedProduct)
		);
		const hasSBGigabitSelected = selectedOptionalProduct.some(
			selectedProduct => checkIfSBGigabitPack(selectedProduct)
		);
		const hasGPONFTTBSelected = selectedOptionalProduct.some(
			selectedProduct => isGPONFTTBPack(selectedProduct)
		);

		if (isOptionalProductSBGigabit && hasPaidGigabitSelected) {
			setDisableRadioForGigabit(true);
			setDisableCheckboxForGigabit(false);
		} else if (isOptionalProductPaidGigabit && hasSBGigabitSelected) {
			setDisableRadioForGigabit(false);
			setDisableCheckboxForGigabit(true);
		} else if (isOptionalGPONFTTBPack && hasGPONFTTBSelected){
			setDisableCheckboxForGigabit(true);
		} else {
			setDisableRadioForGigabit(false);
			setDisableCheckboxForGigabit(false);
		}
	}, [optionalProduct, selectedOptionalProduct]);


	const onCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
		setIsChecked(event.target.checked);
		onChange(optionalProduct, familyName)(event);
	};

	return (
		<tr className="w-tile-table-row">
			<td className="shrink lead"><i className="fa fa-cubes p-2 bg-gray-200"></i></td>
			<td>
				<div className="w-emphasize">{optionalProduct.name?.[intl.locale]}</div>
				<div dangerouslySetInnerHTML={createMarkup(
                    optionalProduct?.long_description?.[intl.locale] ||
                    optionalProduct?.medium_description?.[intl.locale])}
				/>
			</td>
			<td className="text-success text-right">
				<PackagePrices
					fee={extractFee(optionalProduct)}
					fee_type={RECURRENT}
					periodic_amount={optionalProduct.periodic_amount}
					periodic_unit={optionalProduct.periodic_unit}
				/>
			</td>
			{
				!((familyName === config.SUPERPOWER_FAMILY) || (familyName === config.HOME_TV_FAMILY)) ?
					<td className="actions">
						<div className="custom-control custom-checkbox">
							<input
								className="custom-control-input"
								id={checkboxOptionId}
								type="checkbox"
								checked={checked}
								onChange={onCheckboxChange}
								disabled={(checkIfPaidGigabitPack(optionalProduct)) ? disableCheckboxForGigabit : props.disabled}
							/>
							<label className="custom-control-label" htmlFor={checkboxOptionId}></label>
						</div>
					</td> :
					<td className="actions">
						<div className="custom-control custom-radio mb-2 mt-2 radio-button">
							<input
								required
								className="custom-control-input"
								type="radio"
								id={checkboxOptionId}
								checked={checked}
								onChange={onChange(optionalProduct, familyName)}
								disabled={checkIfSBGigabitPack(optionalProduct) ? disableRadioForGigabit : false}
							/>
						</div>
					</td>
			}
		</tr>
	);
};

export { OptionProduct } ;
